'use client';

import { useState, useCallback, useRef, useEffect } from 'react';
import { getAuthHeaders } from '@/utils/apiHeaders';
import { getPublicApiBaseUrl } from '@/utils/publicApiBaseUrl';
import { markLlmStreamActive, markLlmStreamInactive } from '@/utils/activeLlmStreamGuard';

export interface LlmToolCall {
  name: string;
  done: boolean;
  reasoning?: string | null;
}

/**
 * A single chronological segment within a merged multi-turn assistant bubble.
 * Content segments and tool segments interleave in the order they occurred.
 */
export interface LlmMessageSegment {
  type: 'content' | 'tools';
  content?: string;
  tools?: LlmToolCall[];
  /**
   * Stable React key for this segment, derived from the turn it came from rather
   * than its position in the list.
   *
   * Segments are rebuilt from scratch on every render while a response streams, and
   * their indices MOVE: a turn contributes no content segment until its first
   * non-whitespace token arrives, so a tools segment sits at index 0 and then shifts
   * to index 1 mid-stream. Keyed by index, React sees the element at key 0 change
   * component type and remounts it — which destroys the thinking timeline's own
   * expand state on nearly every streamed chunk. That is the "can't expand the
   * thinking, it keeps flickering" report.
   */
  key: string;
}

export interface LlmChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system' | 'tool';
  content: string | null;
  toolCalls?: LlmToolCall[];
  /**
   * For multi-turn merged bubbles: chronologically-ordered content/tool segments.
   * When present, render these instead of content/toolCalls.
   */
  segments?: LlmMessageSegment[];
  /** Progressive people results during execute_audience_search tool execution. */
  progressPeople?: { name: string; email: string | null; company: string | null; phone: string | null; title: string | null; enrichmentStatus?: 'pending' | 'enriching' | 'done' | 'failed' }[];
  /** Total expected people count for skeleton rows. */
  progressTotalExpected?: number;
  /** Current phase of progress: people search or enrichment. */
  progressPhase?: 'people' | 'enrichment';
  /** Enrichment type selected by user. */
  progressEnrichment?: 'none' | 'email' | 'email_phone' | 'both';
}

/**
 * Merge consecutive tool segments — adjacent tool-only turns collapse into one list.
 * Content segments are always kept separate (each is its own paragraph/turn).
 */
function mergeConsecutiveTools(segments: LlmMessageSegment[]): LlmMessageSegment[] {
  const result: LlmMessageSegment[] = [];
  for (const seg of segments) {
    const last = result[result.length - 1];
    if (last?.type === 'tools' && seg.type === 'tools') {
      last.tools = [...(last.tools ?? []), ...(seg.tools ?? [])];
    } else {
      result.push({ ...seg });
    }
  }
  return result;
}

/**
 * Merge all consecutive assistant turns (between user messages) into a single bubble.
 *
 * Single-turn responses (one assistant message) are left unchanged so that
 * same-message tool calls still render after content (content → tools).
 *
 * Multi-turn responses are merged into an ordered `segments` array that
 * interleaves content and tools chronologically — preserving every piece of
 * intermediate content alongside the tool calls it preceded.
 */
function mergeAssistantTurns(messages: LlmChatMessage[]): LlmChatMessage[] {
  const result: LlmChatMessage[] = [];
  let buffer: LlmChatMessage[] = [];

  const flush = () => {
    if (!buffer.length) return;

    if (buffer.length === 1) {
      result.push(buffer[0]);
    } else {
      // Build segments in chronological order: for each turn, content first then tools
      const rawSegments: LlmMessageSegment[] = [];
      for (const m of buffer) {
        if (m.content?.trim()) {
          rawSegments.push({ type: 'content', content: m.content, key: `${m.id}-content` });
        }
        if (m.toolCalls?.length) {
          rawSegments.push({ type: 'tools', tools: m.toolCalls, key: `${m.id}-tools` });
        }
      }
      const segments = mergeConsecutiveTools(rawSegments);
      result.push({
        id: buffer[0].id,
        role: 'assistant',
        content: null,
        segments: segments.length ? segments : undefined,
      });
    }
    buffer = [];
  };

  for (const msg of messages) {
    if (msg.role === 'assistant') {
      buffer.push(msg);
    } else {
      flush();
      result.push(msg);
    }
  }
  flush();

  return result;
}

interface UseLlmChatOptions {
  context?: Record<string, unknown>;
}

/**
 * Stamp the outgoing context with the time of THIS send.
 *
 * Callers build `context.device` once — ChatHub memoises it with an empty
 * dependency list — so `currentTime` would otherwise be frozen at mount. A chat
 * tab left open overnight kept telling the server it was still yesterday, which
 * silently misdates anything resolved from it, reminders included. Timezone and
 * locale are genuinely static for a session; only the clock has to move.
 */
export function withFreshDeviceTime(
  context: Record<string, unknown>,
): Record<string, unknown> {
  const device = context.device;
  if (!device || typeof device !== 'object' || Array.isArray(device)) return context;
  return {
    ...context,
    device: { ...(device as Record<string, unknown>), currentTime: new Date().toISOString() },
  };
}

// No new SSE activity (any chunk, not just recognized types) for this long flips
// isStreamStalled — surfaced as a "still working" notice instead of indefinite
// thinking dots with no signal. Backend agent turns can legitimately run several
// tool-call rounds in sequence (documentation lookups, http_calls), so this is
// deliberately generous — long enough to not fire on normal multi-tool turns,
// short enough that a genuinely stuck turn doesn't look identical to a working one.
const STALL_TIMEOUT_MS = 45_000;

export function useLlmChat(options?: UseLlmChatOptions) {
  const [messages, setMessages] = useState<LlmChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isStreamStalled, setIsStreamStalled] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const isStreamingRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const stallTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearStallTimer = useCallback(() => {
    if (stallTimerRef.current) {
      clearTimeout(stallTimerRef.current);
      stallTimerRef.current = null;
    }
  }, []);

  const resetStallTimer = useCallback(() => {
    clearStallTimer();
    setIsStreamStalled(false);
    stallTimerRef.current = setTimeout(() => setIsStreamStalled(true), STALL_TIMEOUT_MS);
  }, [clearStallTimer]);

  // Safety net for a real (not spurious) unmount mid-stream: stop reading the response body
  // rather than leaving the fetch running and calling setState against a torn-down component.
  // The backend still finishes and persists whatever it already started (this doesn't cancel
  // AI work in progress on the server), it just stops the client from doing pointless work.
  useEffect(() => () => {
    abortRef.current?.abort();
    if (stallTimerRef.current) clearTimeout(stallTimerRef.current);
  }, []);

  const loadMessages = useCallback(async (conversationId: string) => {
    setMessages([]);
    setError(null);
    setIsLoadingMessages(true);
    try {
      const response = await fetch(
        `${getPublicApiBaseUrl()}/private/llm/conversations/${conversationId}/messages`,
        { headers: getAuthHeaders() },
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      type RawMessage = {
        role: string;
        content: string | null;
        tool_calls?: Array<{ function?: { name?: string; arguments?: string } }>;
      };

      const parseArgs = (argsStr: string | undefined): Record<string, unknown> => {
        if (!argsStr) return {};
        try { return JSON.parse(argsStr); } catch { return {}; }
      };

      const flat: LlmChatMessage[] = data.data.messages
        .filter((m: RawMessage) =>
          m.role !== 'system' &&
          m.role !== 'tool' &&
          !(m.role === 'assistant' && !m.content && !m.tool_calls?.length),
        )
        .map((m: RawMessage, i: number) => ({
          id: `loaded-${conversationId}-${i}`,
          role: m.role as LlmChatMessage['role'],
          content: m.content,
          toolCalls: m.tool_calls?.length
            ? m.tool_calls.map((tc) => {
                const args = parseArgs(tc.function?.arguments);
                return {
                  name: tc.function?.name || 'tool',
                  done: true,
                  reasoning: (args.reasoning as string | undefined) || null,
                };
              })
            : undefined,
        }));

      setMessages(mergeAssistantTurns(flat));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load messages');
    } finally {
      setIsLoadingMessages(false);
    }
  }, []);

  // Queue for messages submitted while streaming (e.g. enrichment pill clicks).
  // Processed automatically when the current stream finishes.
  const pendingMessageRef = useRef<{ conversationId: string; content: string } | null>(null);
  // Remembers which conversation the most recent send targeted, so retryLastMessage()
  // (triggered from the error state, with no conversationId in scope at the call site) knows
  // where to resend.
  const lastConversationIdRef = useRef<string | null>(null);

  const sendMessageInternal = useCallback(
    async (conversationId: string, content: string) => {
      // If currently streaming, queue the message and send it when the stream ends.
      // This handles the case where buttons rendered inside a streaming response
      // (enrichment pills, Proceed button) are clicked before the stream fully closes.
      if (isStreamingRef.current) {
        pendingMessageRef.current = { conversationId, content };
        return;
      }

      lastConversationIdRef.current = conversationId;

      const userMsg: LlmChatMessage = {
        id: `user-${Date.now()}`,
        role: 'user',
        content,
      };

      let currentId = `assistant-${Date.now()}`;
      const assistantIds = new Set<string>([currentId]);
      // Tracks tool calls not yet resulted, per bubble. A round can call more than one
      // tool in parallel (parallel_tool_calls) — a new bubble must open only once ALL of
      // that round's tool calls have resulted, not on every individual tool_result. Opening
      // one per tool_result left every result but the last stranded in an empty orphan
      // bubble (no content, no toolCalls) that never received the eventual answer text.
      const pendingToolsByBubble = new Map<string, number>();

      setMessages((prev) => [
        ...prev,
        userMsg,
        { id: currentId, role: 'assistant', content: '' },
      ]);
      isStreamingRef.current = true;
      setIsStreaming(true);
      markLlmStreamActive();
      setError(null);

      abortRef.current = new AbortController();
      resetStallTimer();

      try {
        const response = await fetch(`${getPublicApiBaseUrl()}/private/llm/chat/completions`, {
          method: 'POST',
          headers: getAuthHeaders(),
          signal: abortRef.current.signal,
          body: JSON.stringify({
            messages: [{ role: 'user', content }],
            stream: true,
            conversation_id: conversationId,
            ...(options?.context ? { context: withFreshDeviceTime(options.context) } : {}),
          }),
        });

        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          throw new Error((body as { error?: string }).error || `HTTP ${response.status}`);
        }

        const reader = response.body!.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

         
        while (true) {
           
          const { done, value } = await reader.read();
          if (done) break;
          resetStallTimer();

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop()!;

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            const raw = line.slice(6).trim();
            if (raw === '[DONE]') break;

            try {
              const chunk = JSON.parse(raw) as {
                type?: string;
                name?: string;
                reasoning?: string | null;
                choices?: Array<{ delta?: { content?: string } }>;
                error?: string;
                people?: unknown[];
                totalExpected?: number;
                phase?: string;
                enrichment?: string;
              };

              if (chunk.error) {
                throw new Error(chunk.error);
              }

              // Tool call — always appended to the current bubble.
              if (chunk.type === 'tool_call' && chunk.name) {
                const capturedId = currentId;
                pendingToolsByBubble.set(capturedId, (pendingToolsByBubble.get(capturedId) ?? 0) + 1);
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === capturedId
                      ? {
                          ...m,
                          toolCalls: [
                            ...(m.toolCalls ?? []),
                            { name: chunk.name!, done: false, reasoning: chunk.reasoning ?? null },
                          ],
                        }
                      : m,
                  ),
                );
                continue;
              }

              // Tool progress — incremental results during tool execution
              if (chunk.type === 'tool_progress' && chunk.people) {
                const capturedId = currentId;
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === capturedId
                      ? {
                          ...m,
                          progressPeople: chunk.people as LlmChatMessage['progressPeople'],
                          progressTotalExpected: chunk.totalExpected as number | undefined,
                          progressPhase: (chunk.phase as 'people' | 'enrichment') || 'people',
                          progressEnrichment: (chunk.enrichment as LlmChatMessage['progressEnrichment']) || undefined,
                        }
                      : m,
                  ),
                );
                continue;
              }

              if (chunk.type === 'tool_result' && chunk.name) {
                const capturedId = currentId;
                setMessages((prev) =>
                  prev.map((m) => {
                    if (m.id !== capturedId || !m.toolCalls) return m;
                    const idx = m.toolCalls.findIndex((t) => !t.done && t.name === chunk.name);
                    if (idx === -1) return m;
                    return {
                      ...m,
                      // Keep progressPeople so the table stays visible until final AUDIENCE_RESULTS marker renders
                      toolCalls: m.toolCalls.map((t, j) => (j === idx ? { ...t, done: true } : t)),
                    };
                  }),
                );
                // If the completed tool was an http_call, the workflow may have changed — notify the canvas to re-fetch.
                if (chunk.name === 'http_call' && typeof window !== 'undefined') {
                  window.dispatchEvent(new CustomEvent('workflow-modified', { detail: { tool: 'http_call' } }));
                }
                // Only open the next bubble once every tool call from THIS round has
                // resulted — a round with parallel tool calls fires one tool_result per
                // tool, and only the last one should hand off to a fresh bubble.
                const remaining = Math.max(0, (pendingToolsByBubble.get(capturedId) ?? 1) - 1);
                pendingToolsByBubble.set(capturedId, remaining);
                if (remaining === 0) {
                  const newId = `assistant-${Date.now()}-${Math.random().toString(36).slice(2)}`;
                  currentId = newId;
                  assistantIds.add(newId);
                  setMessages((prev) => [...prev, { id: newId, role: 'assistant', content: '' }]);
                }
                continue;
              }

              const deltaContent = chunk.choices?.[0]?.delta?.content;
              if (deltaContent) {
                const capturedId = currentId;
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === capturedId
                      ? { ...m, content: (m.content || '') + deltaContent }
                      : m,
                  ),
                );
              }
            } catch (parseErr) {
              if (parseErr instanceof SyntaxError) {
                console.warn('[useLlmChat] malformed SSE chunk, skipping:', raw, parseErr);
                continue;
              }
              throw parseErr;
            }
          }
        }
      } catch (err: unknown) {
        if (err instanceof Error && err.name === 'AbortError') return;
        const msg = err instanceof Error ? err.message : 'Stream failed';
        setError(msg);
        setMessages((prev) => prev.filter((m) => !assistantIds.has(m.id)));
      } finally {
        isStreamingRef.current = false;
        setIsStreaming(false);
        clearStallTimer();
        setIsStreamStalled(false);
        markLlmStreamInactive();
        // Remove empty assistant messages, then merge consecutive turns.
        // Preserve previously-merged messages (content:null + segments) from earlier turns.
        setMessages((prev) => {
          const cleaned = prev.filter(
            (m) =>
              m.role !== 'assistant' ||
              (m.content && m.content.trim()) ||
              m.toolCalls?.length ||
              m.segments?.length,
          );
          return mergeAssistantTurns(cleaned);
        });
        abortRef.current = null;

        // Process any message that was queued while streaming
        const pending = pendingMessageRef.current;
        if (pending) {
          pendingMessageRef.current = null;
          // Use setTimeout to let React flush state updates from the finally block
          // before starting the next send cycle
          setTimeout(() => sendMessageInternalRef.current(pending.conversationId, pending.content), 0);
        }
      }
    },
    [options, resetStallTimer, clearStallTimer],
  );

  // Wrap in a stable ref so the finally-block setTimeout always calls the latest version
  const sendMessageInternalRef = useRef(sendMessageInternal);
  sendMessageInternalRef.current = sendMessageInternal;

  const sendMessage = useCallback(
    (conversationId: string, content: string) => sendMessageInternalRef.current(conversationId, content),
    [],
  );

  // Resend the last user message after a failed send, without duplicating it in the
  // transcript: drops that message (and the empty/failed assistant turn after it, if any)
  // before resubmitting, so "Try again" replaces the failed attempt rather than appending
  // a second copy of the same question.
  const retryLastMessage = useCallback(() => {
    const conversationId = lastConversationIdRef.current;
    if (!conversationId) return;
    setMessages((prev) => {
      const idx = prev.map((m) => m.role).lastIndexOf('user');
      if (idx === -1) return prev;
      const content = prev[idx].content ?? '';
      setTimeout(() => sendMessageInternalRef.current(conversationId, content), 0);
      return prev.slice(0, idx);
    });
  }, []);

  const stopStreaming = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const persistMessages = useCallback(
    async (conversationId: string, messages: Array<{ role: string; content: string; metadata?: Record<string, unknown> }>) => {
      const response = await fetch(`${getPublicApiBaseUrl()}/private/llm/conversations/${conversationId}/messages`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ messages }),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
    },
    [],
  );

  const clearMessages = useCallback(() => {
    setMessages([]);
    setError(null);
  }, []);

  return { messages, isStreaming, isStreamStalled, isLoadingMessages, error, loadMessages, sendMessage, retryLastMessage, stopStreaming, clearMessages, persistMessages };
}
